/**
 * SUNTRONIX ONE — Stub contract cleanup queue.
 *
 * Every contract born as a Preflight quick-stub (`is_stub: true`) lands
 * here so finance can close the loop.  Sorted oldest-first so the debt
 * doesn't accumulate.
 */
import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { FileSignature, ExternalLink, RotateCcw, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function StubContracts() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(true);
  const [includeClosed, setIncludeClosed] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/contract-centre/contracts/stubs",
        { params: { include_closed: includeClosed } });
      setRows(data.items || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  }, [includeClosed]);
  useEffect(() => { load(); }, [includeClosed, load]);

  const oldStubs = rows.filter((r) => (r.age_days || 0) >= 7).length;

  return (
    <div className="space-y-4 max-w-[1400px]" data-testid="stub-contracts-page">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">Commercial · Cleanup</div>
          <h1 className="text-2xl font-heading font-semibold text-slate-900 tracking-tight">Stub contracts</h1>
          <p className="text-[13px] text-slate-500 mt-1 max-w-2xl">
            Contracts auto-created from a Preflight <b>Log stub contract</b> action.
            Finance should replace each with a properly-papered contract, then move
            the stub to Terminated.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-[12px] text-slate-600 flex items-center gap-1.5">
            <input type="checkbox" checked={includeClosed} onChange={(e) => setIncludeClosed(e.target.checked)}
              data-testid="stubs-include-closed" />
            Include closed
          </label>
          <Button size="sm" variant="outline" onClick={load} data-testid="stubs-refresh">
            <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Refresh
          </Button>
        </div>
      </div>

      {oldStubs > 0 && (
        <div className="rounded border border-amber-200 bg-amber-50 p-3 flex items-start gap-2 text-[12px] text-amber-900">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <div><b>{oldStubs} stub{oldStubs > 1 ? "s" : ""}</b> are more than 7 days old — chase commercial for the real paperwork.</div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-[10px] uppercase text-slate-500">
              <th className="text-left px-3 py-2.5">Contract</th>
              <th className="text-left px-3 py-2.5">Customer</th>
              <th className="text-left px-3 py-2.5">Age</th>
              <th className="text-left px-3 py-2.5">Currency</th>
              <th className="text-left px-3 py-2.5">Entitlements</th>
              <th className="text-left px-3 py-2.5">Reason</th>
              <th className="text-left px-3 py-2.5">Created by</th>
              <th className="text-right px-3 py-2.5">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {busy && rows.length === 0 ? (
              <tr><td colSpan={8} className="px-3 py-6 text-center"><Loader2 className="h-4 w-4 mx-auto animate-spin" /></td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-400 text-[13px]">
                <FileSignature className="h-6 w-6 mx-auto mb-2 opacity-50" />
                No stub contracts.  Everything is properly papered — nice.
              </td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} data-testid={`stub-row-${r.id}`} className="hover:bg-slate-50">
                <td className="px-3 py-2">
                  <div className="text-[13px] font-mono text-slate-900">{r.contract_number}</div>
                  <div className="text-[10px] text-slate-500">{r.contract_name}</div>
                </td>
                <td className="px-3 py-2 text-xs text-slate-700">{r.master_company_name || "—"}</td>
                <td className="px-3 py-2">
                  <AgeChip days={r.age_days} />
                </td>
                <td className="px-3 py-2 text-xs">{r.currency}</td>
                <td className="px-3 py-2 text-xs">{r.entitlement_count}</td>
                <td className="px-3 py-2 text-[12px] text-slate-600 max-w-[220px]">
                  <div className="line-clamp-2" title={r.stub_reason}>{r.stub_reason || "—"}</div>
                </td>
                <td className="px-3 py-2 text-xs font-mono">{r.created_by}</td>
                <td className="px-3 py-2 text-right">
                  <Link to={`/commercial/contracts?id=${r.id}`}
                    className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-slate-200 hover:bg-slate-100"
                    data-testid={`stub-open-${r.id}`}>
                    <ExternalLink className="h-3 w-3" /> Open
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AgeChip({ days }) {
  if (days == null) return <span className="text-slate-400">—</span>;
  let tone = "bg-emerald-100 text-emerald-800";
  if (days >= 14) tone = "bg-rose-100 text-rose-800";
  else if (days >= 7) tone = "bg-amber-100 text-amber-800";
  return (
    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${tone}`}>
      {days} {days === 1 ? "day" : "days"}
    </span>
  );
}
